// Phase 12/13 — derive lead-attribution fields from the current URL.
//
// Critical for Facebook ads attribution: leads submitted from /facebook-landing
// must be visually identifiable in the team inbox (via subject tag
// `[FACEBOOK AD]` emitted by the backend) AND must carry the full URL +
// referrer so spend ROI can be reconciled against Facebook Ads Manager.
//
// All helpers are pure (no network, no React state) so they're safe to call
// from any submit handler. Backend canonical `source` enum lives in
// server._SOURCES; keep this map in sync with it.

export function deriveSource() {
  if (typeof window === "undefined") return "unknown";
  const path = (window.location.pathname || "").replace(/\/+$/, "") || "/";
  if (path === "/facebook-landing") return "facebook_landing";
  if (path === "/contact") return "contact_page";
  if (path.startsWith("/solar-calculator")) return "calculator_lead";
  // Phase 36 — Google Solar API roof + savings checker.
  if (path.startsWith("/solar-panel-checker")) return "solar_checker";
  // Phase 34 — export capacity checker (hub + per-area pages).
  if (path.startsWith("/solar-export-limits")) return "export_check";
  // Phase 23 — Canstar affiliate energy-switch email capture.
  if (path === "/energy-switch") return "energy_switch";
  if (path === "/") return "hero_form";
  return "unknown";
}

/** Full URL at submission time including protocol, host, path, and querystring.
 *  Preserves utm_* and any other paid-traffic attribution params verbatim.
 *  Drops the hash (`#section-1`) — not useful for attribution. */
export function buildSourceUrl() {
  if (typeof window === "undefined") return null;
  const { protocol, host, pathname, search } = window.location;
  return `${protocol}//${host}${pathname || "/"}${search || ""}`;
}

/** Bare pathname only (e.g. `/facebook-landing`). Used in the email subject
 *  prefix logic + at-a-glance staff lookups. */
export function buildSourcePath() {
  if (typeof window === "undefined") return null;
  return window.location.pathname || "/";
}

/** Document referrer — the URL the visitor was on BEFORE this page. Critical
 *  for attributing leads that landed on `/contact` after clicking a CTA from
 *  a `/solar-installers/{city}` location page (no inline form on those
 *  pages). Returns null when the visitor arrived directly. */
export function getReferrer() {
  if (typeof document === "undefined") return null;
  const ref = (document.referrer || "").trim();
  return ref || null;
}
